import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as DeviceStatusIcon } from '../../../assets/icons/dot-circle.svg';
import { AccountDevicesInterFace } from '../../../interface/account-devices-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAccountDevices, setSortData } from '../../../redux/slices/AccountDeviceSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';

interface StationsProperties {
  onClose: () => void;
  isDeviceOffline?: boolean;
}
/**
 * Stations table component
 */
function Devices({ onClose, isDeviceOffline }: StationsProperties) {
  const { t } = useTranslation('account');
  const [searchParameters] = useSearchParams();
  const accountCustomId = searchParameters.get('accountCustomId');
  const stationReference = useRef<HTMLDivElement | null>(null);
  const { accountDevices, limit, page, loading, totalPages, totalResults, sortBy, sortField } =
    useAppSelector((state) => state.accountDevice);
  const dispatch = useAppDispatch();
  const [selectedStation, setSelectedStation] = useState<AccountDevicesInterFace | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: AccountDevicesInterFace) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  const column = [
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof AccountDevicesInterFace,
      /**
       *  Side panel component to show on clicking the cell value
       */
      customData: (device: AccountDevicesInterFace) => (
        <button
          type="button"
          className="accounts-management"
          onClick={() => {
            setSelectedStation(device);
            setIsPanelOpen(true);
          }}>
          {device.chargingPointSn}
        </button>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof AccountDevicesInterFace,
      customData: renderStatus,
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof AccountDevicesInterFace,
    },
  ];

  /**
   * Fetch Devices list
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchAccountDevices({ accountCustomId, ...data }));
    }
  };

  /**
   * sort function
   */
  const handleSortChange = (sortData: any) => {
    dispatch(
      setSortData({
        sortField: sortData.sortField,
        sortBy: sortData.sortBy,
      }),
    );

    // 2️⃣ Call API with sort params
    fetchData({
      page: 1, // reset page on sort
      limit,
      sortField: sortData.sortField,
      sortBy: sortData.sortBy,
      ...(typeof isDeviceOffline === 'boolean' ? { isDeviceOffline } : {}),
    });
  };

  /**
   * Table pagination function
   */
  const handlePageOrLimitChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({
      ...value,
      sortField,
      sortBy,
    });
  };

  // Fetch when accountCustomId, page, or limit changes
  useEffect(() => {
    if (accountDevices.length === 0 || accountCustomId) {
      fetchData({
        page,
        limit,
        hardRefresh: true,
        sortField,
        sortBy,
        ...(typeof isDeviceOffline === 'boolean' ? { isDeviceOffline } : {}),
      });
    }
  }, [accountCustomId, isDeviceOffline, page, limit, sortField, sortBy]);

  //  Close panel when clicked outside
  useEffect(() => {
    /**
     * Handle click outside the component to trigger onClose
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (stationReference.current && !stationReference.current.contains(event.target as Node)) {
        if (isPanelOpen) {
          setIsPanelOpen(false);
          setSelectedStation(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPanelOpen, onClose]);

  return (
    <div ref={stationReference} className="devices-content">
      <PageTitle
        title={t('devices')}
        buttonSvgIcon={<CloseIcon />}
        buttonType="none"
        onClickButton={() => {
          if (isPanelOpen) {
            setIsPanelOpen(false);
            setSelectedStation(null);
          } else {
            onClose();
          }
        }}
        buttonLabel=" "
      />
      {isPanelOpen && selectedStation ? (
        <DeviceSidePanel fetchIdFrom="props" id={selectedStation._id} />
      ) : (
        <CustomTable
          header={column}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortChange}
          limit={limit}
          totalPages={totalPages}
          data={accountDevices}
          page={page}
          loading={loading}
          totalResults={totalResults}
          pageLimitChange={handlePageOrLimitChange}
          height={370}
        />
      )}
    </div>
  );
}

Devices.defaultProps = {
  isDeviceOffline: null,
};
export default Devices;
